// src/components/coach/CoachPathwaysCard.jsx
//
// The coach block of an org's Help & Support Pathways (data/supportPathways.js):
// what to do when a player comes to a coach. Listen, respond, connect — never
// counsel. Sits on the team dashboard so the coach who needs it in the car park
// after practice can find it in two taps. Collapsed by default; the heading
// alone says what it is.

import React, { useState } from 'react';
import { HeartHandshake, ChevronDown, Ear, Ban, ArrowRightCircle, Siren, Flag } from 'lucide-react';
import CrisisResources from '../dashboard/CrisisResources';
import { useOrgVoice } from '../../hooks/useOrgVoice';

const Block = ({ icon: Icon, title, tone = 'slate', children }) => (
    <div className={`rounded-2xl border p-4 ${tone === 'rose' ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'}`}>
        <p className={`text-[11px] font-black uppercase tracking-widest flex items-center gap-2 mb-2 ${tone === 'rose' ? 'text-rose-700' : 'text-slate-500'}`}>
            <Icon size={14} /> {title}
        </p>
        {children}
    </div>
);

const Bullets = ({ items = [], quote = false }) => (
    <ul className="space-y-1.5">
        {items.map((it, i) => (
            <li key={i} className="text-sm text-slate-700 font-medium leading-snug flex gap-2">
                <span className="text-brand-primary shrink-0">{quote ? '“' : '•'}</span>
                <span>{quote ? `${it}”` : it}</span>
            </li>
        ))}
    </ul>
);

const CoachPathwaysCard = ({ pathways }) => {
    const { t } = useOrgVoice();
    const [open, setOpen] = useState(false);
    const coach = pathways?.coach;
    if (!coach) return null;

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between gap-4 p-6 md:p-8 text-left"
            >
                <div className="flex items-center gap-4 min-w-0">
                    <div className="w-11 h-11 rounded-2xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><HeartHandshake size={22} /></div>
                    <div className="min-w-0">
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Support</p>
                        <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight leading-tight">{coach.title}</h3>
                        {!open && <p className="text-sm text-slate-500 font-medium mt-0.5 truncate">Listen, respond, connect. Not counsel.</p>}
                    </div>
                </div>
                <ChevronDown size={20} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="px-6 md:px-8 pb-6 md:pb-8 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    <p className="text-sm text-slate-600 font-medium leading-relaxed">{coach.intro}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {coach.listen && (
                            <Block icon={Ear} title={coach.listen.title}>
                                <p className="text-sm text-slate-600 font-medium mb-2">{coach.listen.text}</p>
                                <Bullets items={coach.listen.lines} quote />
                            </Block>
                        )}
                        {coach.avoid && (
                            <Block icon={Ban} title={coach.avoid.title}>
                                <Bullets items={coach.avoid.items} />
                                {coach.avoid.note && <p className="text-xs text-slate-500 font-medium leading-relaxed mt-3">{coach.avoid.note}</p>}
                            </Block>
                        )}
                        {coach.next && (
                            <Block icon={ArrowRightCircle} title={coach.next.title}>
                                <p className="text-sm text-slate-600 font-medium">{coach.next.text}</p>
                                <p className="text-sm font-black text-slate-900 my-2">“{coach.next.ask}”</p>
                                {coach.next.note && <p className="text-xs text-slate-500 font-medium leading-relaxed">{coach.next.note}</p>}
                            </Block>
                        )}
                        {coach.escalation && (
                            <Block icon={Flag} title={coach.escalation.title}>
                                <p className="text-sm text-slate-600 font-medium mb-2">{coach.escalation.text}</p>
                                <div className="space-y-1.5">
                                    {(coach.escalation.routes || []).map((r, i) => (
                                        <p key={i} className="text-sm text-slate-700 font-medium leading-snug">{r.href ? <a href={r.href} target="_blank" rel="noopener noreferrer" className="font-black text-brand-primary hover:underline">{r.label}</a> : <span className="font-black text-slate-900">{r.label}</span>} — {r.text}</p>
                                    ))}
                                </div>
                            </Block>
                        )}
                    </div>

                    {coach.urgent && (
                        <Block icon={Siren} title={coach.urgent.title} tone="rose">
                            <p className="text-sm text-slate-700 font-medium mb-2">{coach.urgent.text}</p>
                            <Bullets items={coach.urgent.items} />
                        </Block>
                    )}

                    <div className="rounded-2xl border border-slate-200 p-4">
                        <CrisisResources ageTier="teen" heading={t('coachLinesHeading')} compact />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CoachPathwaysCard;
